import type { ReactNode } from 'react';

export interface UserChipProps {
  name: string;
  /** Small grey caption under the name (e.g. the email). Never a role: users are not shown their role. */
  caption?: string;
  /**
   * Rendered inline immediately after the name, e.g. a status badge. Optional
   * and additive: existing call sites are unaffected.
   *
   * A badge placed as a SIBLING of the whole chip instead drifts to the right
   * of the avatar/name/caption block and reads as belonging to the next column.
   * Beside the name is where it is actually about.
   */
  nameSuffix?: ReactNode;
}

function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

/** Avatar initials circle + name + optional caption. */
export function UserChip({ name, caption, nameSuffix }: UserChipProps) {
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green font-sans text-sm font-semibold text-white"
      >
        {initialsOf(name)}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-sans text-sm font-semibold text-navy">{name}</span>
          {nameSuffix}
        </span>
        {caption && (
          <span className="text-xs text-slate" title={caption}>
            {caption}
          </span>
        )}
      </span>
    </div>
  );
}
